// src/store/p2pStore.ts
// ─────────────────────────────────────────────────────────────────────────────
// NIIZ ERP · Store de Conexión P2P (Multi-Dispositivo / Multi-Sede)
// ─────────────────────────────────────────────────────────────────────────────
// Actualización v110:
//   · Cero `any` — tipado estricto en todas las interfaces
//   · Soporte multi-sede (branch_id) en peers y sincronización
//   · Clasificación flexible (tags) por dispositivo
//   · Conexión con motor financiero (snapshot hash, sync stats)
//   · Estado de sincronización por tabla (qué datos están al día)
//   · 100% aditivo: toda la lógica de negocio existente se preserva
// ─────────────────────────────────────────────────────────────────────────────

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ─────────────────────────────────────────────────────────────
// TIPOS
// ─────────────────────────────────────────────────────────────

export type TerminalType = 'CASHIER' | 'KDS' | 'WAITER' | 'BAR' | 'WAREHOUSE' | 'MANAGER' | 'ACCOUNTANT';
export type P2PRole = 'HOST' | 'CLIENT' | 'AUTO';
export type SyncStatus = 'SYNCED' | 'PENDING' | 'CONFLICT' | 'ERROR';

/** Estado de sincronización de una tabla específica. */
export interface TableSyncState {
  tableName: string;
  status: SyncStatus;
  lastSyncAt: string | null;
  pendingRows: number;
  conflicts: number;
}

/** Estadísticas de sincronización P2P. */
export interface SyncStats {
  messagesSent: number;
  messagesReceived: number;
  bytesTransferred: number;
  lastMessageAt: string | null;
  averageLatencyMs: number;
}

/** Métricas extendidas de un peer conectado. */
export interface ConnectedPeer {
  id: string;
  name: string;
  terminalType: TerminalType;
  latencyMs: number;
  joinedAt: string;
  isHost?: boolean;
  
  // ── 🆕 MULTI-SEDE Y CLASIFICACIÓN v110 ──
  /** ID de la sucursal donde opera este peer (multi-sede). */
  branch_id?: string;
  /** Etiquetas dinámicas para clasificación flexible (ej: ["caja-principal", "turno-noche"]). */
  tags?: string[];
  
  // ── 🆕 MÉTRICAS DE SINCRONIZACIÓN v110 ──
  /** Última vez que este peer sincronizó datos financieros. */
  lastFinancialSyncAt?: string | null;
  /** Hash del último snapshot financiero recibido de este peer. */
  lastSnapshotHash?: string | null;
  /** Número de mensajes intercambiados con este peer. */
  messageCount?: number;
}

interface P2PState {
  // Estado de conexión
  isP2PEnabled: boolean;
  isConnecting: boolean;
  isConnected: boolean;
  myPeerId: string;
  pairingCode: string;
  businessId: string;
  deviceName: string;
  terminalType: TerminalType;
  role: P2PRole;
  connectedPeers: ConnectedPeer[];
  lastSyncAt: string | null;
  isModalOpen: boolean;

  // ── 🆕 ESTADO MULTI-SEDE Y FINANCIERO v110 ──
  /** ID de la sucursal activa para sincronización P2P. */
  activeBranchId: string | 'ALL';
  /** Estadísticas globales de sincronización. */
  syncStats: SyncStats;
  /** Hash del último snapshot financiero sincronizado. */
  lastFinancialSnapshotHash: string | null;
  /** Estado de sincronización por tabla. */
  tableSyncStatus: Record<string, TableSyncState>;

  // Acciones básicas
  setModalOpen: (open: boolean) => void;
  setP2PEnabled: (enabled: boolean) => void;
  setConnecting: (connecting: boolean) => void;
  setConnected: (connected: boolean) => void;
  setMyPeerId: (id: string) => void;
  setPairingCode: (code: string) => void;
  setBusinessId: (id: string) => void;
  setDeviceName: (name: string) => void;
  setTerminalType: (type: TerminalType) => void;
  setRole: (role: P2PRole) => void;
  setConnectedPeers: (peers: ConnectedPeer[]) => void;
  addPeer: (peer: ConnectedPeer) => void;
  removePeer: (peerId: string) => void;
  updatePeerLatency: (peerId: string, latencyMs: number) => void;
  setLastSyncAt: (date: string) => void;

  // ── 🆕 ACCIONES MULTI-SEDE Y FINANCIERAS v110 ──
  /** Cambia la sede activa para sincronización P2P. */
  setActiveBranch: (branchId: string | 'ALL') => void;
  /** Actualiza las estadísticas globales de sincronización. */
  updateSyncStats: (stats: Partial<SyncStats>) => void;
  /** Incrementa los contadores de mensajes. */
  incrementMessageCount: (direction: 'sent' | 'received', bytes?: number) => void;
  /** Actualiza el estado de sincronización de una tabla. */
  setTableSyncStatus: (tableName: string, status: TableSyncState) => void;
  /** Registra el hash del último snapshot financiero sincronizado. */
  setFinancialSnapshotHash: (hash: string) => void;
  /** Actualiza métricas de un peer específico. */
  updatePeerMetrics: (peerId: string, metrics: Partial<ConnectedPeer>) => void;

  // ── 🆕 HELPERS ──
  /** Retorna peers filtrados por sede. */
  getPeersByBranch: (branchId: string) => ConnectedPeer[];
  /** Verifica si un peer está en la misma sede que el dispositivo actual. */
  isPeerInSameBranch: (peerId: string) => boolean;
  /** Calcula un puntaje de salud de sincronización (0-100). */
  getSyncHealthScore: () => number;
}

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

const getDefaultDeviceName = (): string => {
  if (typeof window === 'undefined') return 'Terminal NIIZ';
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return 'Móvil Apple';
  if (/Android/.test(ua)) return 'Móvil Android';
  if (/Windows/.test(ua)) return 'PC Windows';
  if (/Macintosh/.test(ua)) return 'Mac NIIZ';
  return 'Terminal POS';
};

const getDefaultSyncStats = (): SyncStats => ({
  messagesSent: 0,
  messagesReceived: 0,
  bytesTransferred: 0,
  lastMessageAt: null,
  averageLatencyMs: 0,
});

// ─────────────────────────────────────────────────────────────
// STORE P2P
// ─────────────────────────────────────────────────────────────

export const useP2PStore = create<P2PState>()(
  persist(
    (set, get) => ({
      // Estado de conexión
      isP2PEnabled: true,
      isConnecting: false,
      isConnected: false,
      myPeerId: '',
      pairingCode: '',
      businessId: 'local',
      deviceName: getDefaultDeviceName(),
      terminalType: 'CASHIER',
      role: 'AUTO',
      connectedPeers: [],
      lastSyncAt: null,
      isModalOpen: false,

      // ── 🆕 Estado multi-sede y financiero v110 ──
      activeBranchId: 'ALL',
      syncStats: getDefaultSyncStats(),
      lastFinancialSnapshotHash: null,
      tableSyncStatus: {},

      // Acciones básicas
      setModalOpen: (open) => set({ isModalOpen: open }),
      setP2PEnabled: (enabled) => set({ isP2PEnabled: enabled }),
      setConnecting: (connecting) => set({ isConnecting: connecting }),
      setConnected: (connected) => set({ isConnected: connected }),
      setMyPeerId: (id) => set({ myPeerId: id }),
      setPairingCode: (code) => set({ pairingCode: code }),
      setBusinessId: (id) => set({ businessId: id }),
      setDeviceName: (name) => set({ deviceName: name }),
      setTerminalType: (type) => set({ terminalType: type }),
      setRole: (role) => set({ role }),
      setConnectedPeers: (peers) => set({ connectedPeers: peers }),
      
      addPeer: (peer) =>
        set((state) => ({
          connectedPeers: [
            ...state.connectedPeers.filter((p) => p.id !== peer.id),
            peer,
          ],
        })),
      
      removePeer: (peerId) =>
        set((state) => ({
          connectedPeers: state.connectedPeers.filter((p) => p.id !== peerId),
        })),
      
      updatePeerLatency: (peerId, latencyMs) =>
        set((state) => ({
          connectedPeers: state.connectedPeers.map((p) =>
            p.id === peerId ? { ...p, latencyMs } : p,
          ),
        })),
      
      setLastSyncAt: (date) => set({ lastSyncAt: date }),

      // ── 🆕 Acciones multi-sede y financieras v110 ──

      setActiveBranch: (branchId) => set({ activeBranchId: branchId }),

      updateSyncStats: (stats) =>
        set((state) => ({
          syncStats: { ...state.syncStats, ...stats },
        })),

      incrementMessageCount: (direction, bytes = 0) =>
        set((state) => {
          const now = new Date().toISOString();
          const newStats = { ...state.syncStats };
          
          if (direction === 'sent') {
            newStats.messagesSent += 1;
          } else {
            newStats.messagesReceived += 1;
          }
          
          newStats.bytesTransferred += bytes;
          newStats.lastMessageAt = now;
          
          return { syncStats: newStats };
        }),

      setTableSyncStatus: (tableName, status) =>
        set((state) => ({
          tableSyncStatus: {
            ...state.tableSyncStatus,
            [tableName]: status,
          },
        })),

      setFinancialSnapshotHash: (hash) =>
        set({ lastFinancialSnapshotHash: hash }),

      updatePeerMetrics: (peerId, metrics) =>
        set((state) => ({
          connectedPeers: state.connectedPeers.map((p) =>
            p.id === peerId ? { ...p, ...metrics } : p,
          ),
        })),

      // ── 🆕 Helpers ──

      getPeersByBranch: (branchId) => {
        const { connectedPeers } = get();
        return connectedPeers.filter((p) => p.branch_id === branchId);
      },

      isPeerInSameBranch: (peerId) => {
        const { connectedPeers, activeBranchId } = get();
        if (activeBranchId === 'ALL') return true;
        
        const peer = connectedPeers.find((p) => p.id === peerId);
        if (!peer) return false;
        
        return peer.branch_id === activeBranchId;
      },

      getSyncHealthScore: () => {
        const { connectedPeers, syncStats, tableSyncStatus } = get();
        
        if (connectedPeers.length === 0) return 0;
        
        let score = 100;
        
        // Penalizar por latencia alta (>500ms)
        const avgLatency = connectedPeers.reduce((sum, p) => sum + p.latencyMs, 0) / connectedPeers.length;
        if (avgLatency > 500) score -= 30;
        else if (avgLatency > 200) score -= 15;
        
        // Penalizar por conflictos de sincronización
        const tables = Object.values(tableSyncStatus);
        const conflicts = tables.reduce((sum, t) => sum + t.conflicts, 0);
        if (conflicts > 0) score -= Math.min(40, conflicts * 10);
        
        // Penalizar por filas pendientes
        const pending = tables.reduce((sum, t) => sum + t.pendingRows, 0);
        if (pending > 100) score -= 20;
        else if (pending > 50) score -= 10;
        
        // Bonus por actividad reciente
        if (syncStats.lastMessageAt) {
          const lastMsg = new Date(syncStats.lastMessageAt).getTime();
          const now = Date.now();
          const minutesSinceLastMsg = (now - lastMsg) / (1000 * 60);
          if (minutesSinceLastMsg < 5) score += 10;
        }
        
        return Math.max(0, Math.min(100, score));
      },
    }),
    {
      name: 'niiz-p2p-storage',
      version: 2, // Incrementamos para migración de campos nuevos
      
      partialize: (state) => ({
        deviceName: state.deviceName,
        terminalType: state.terminalType,
        role: state.role,
        pairingCode: state.pairingCode,
        isP2PEnabled: state.isP2PEnabled,
        // ── 🆕 Persistir estado multi-sede ──
        activeBranchId: state.activeBranchId,
        lastFinancialSnapshotHash: state.lastFinancialSnapshotHash,
      }),

      // Migración de versión 1 a 2 (añadir campos nuevos)
      migrate: (persistedState: unknown, version: number) => {
        if (version < 2 && persistedState && typeof persistedState === 'object') {
          // Se clona el estado persistido (JSON de localStorage) antes de tocarlo.
          const patch: Record<string, unknown> = { ...persistedState };
          // Rellenar campos nuevos con defaults
          patch['activeBranchId'] = 'ALL';
          patch['lastFinancialSnapshotHash'] = null;
          return patch;
        }
        return persistedState;
      },
    },
  ),
);

// ─────────────────────────────────────────────────────────────
// HELPERS PÚBLICOS (fuera del store para uso en componentes)
// ─────────────────────────────────────────────────────────────

/**
 * Formatea el nombre legible de un tipo de terminal.
 */
export const formatTerminalType = (type: TerminalType): string => {
  const labels: Record<TerminalType, string> = {
    CASHIER: 'Caja',
    KDS: 'Cocina (KDS)',
    WAITER: 'Mesero',
    BAR: 'Barra',
    WAREHOUSE: 'Bodega',
    MANAGER: 'Gerencia',
    ACCOUNTANT: 'Contabilidad',
  };
  return labels[type] || type;
};

/**
 * Formatea el nombre legible de un rol P2P.
 */
export const formatP2PRole = (role: P2PRole): string => {
  const labels: Record<P2PRole, string> = {
    HOST: 'Servidor (Host)',
    CLIENT: 'Cliente',
    AUTO: 'Automático',
  };
  return labels[role] || role;
};

/**
 * Calcula la latencia promedio de todos los peers conectados.
 */
export const calculateAverageLatency = (peers: ConnectedPeer[]): number => {
  if (peers.length === 0) return 0;
  const sum = peers.reduce((acc, p) => acc + p.latencyMs, 0);
  return Math.round(sum / peers.length);
};

/**
 * Verifica si la conexión P2P está saludable (latencia < 300ms, sin conflictos).
 */
export const isP2PHealthy = (state: P2PState): boolean => {
  if (!state.isConnected || state.connectedPeers.length === 0) return false;
  
  const avgLatency = calculateAverageLatency(state.connectedPeers);
  if (avgLatency > 300) return false;
  
  const conflicts = Object.values(state.tableSyncStatus).reduce(
    (sum, t) => sum + t.conflicts,
    0,
  );
  
  return conflicts === 0;
};

/**
 * Genera un resumen de estado P2P para mostrar en la UI.
 */
export const getP2PStatusSummary = (state: P2PState): {
  status: 'connected' | 'connecting' | 'disconnected' | 'error';
  message: string;
  peerCount: number;
} => {
  if (!state.isP2PEnabled) {
    return { status: 'disconnected', message: 'P2P desactivado', peerCount: 0 };
  }
  
  if (state.isConnecting) {
    return { status: 'connecting', message: 'Conectando...', peerCount: 0 };
  }
  
  if (!state.isConnected) {
    return { status: 'disconnected', message: 'Sin conexión', peerCount: 0 };
  }
  
  const peerCount = state.connectedPeers.length;
  if (peerCount === 0) {
    return { status: 'connected', message: 'Conectado (sin peers)', peerCount: 0 };
  }
  
  const healthy = isP2PHealthy(state);
  if (!healthy) {
    return { status: 'error', message: 'Conexión inestable', peerCount };
  }
  
  return {
    status: 'connected',
    message: `Conectado con ${peerCount} dispositivo${peerCount > 1 ? 's' : ''}`,
    peerCount,
  };
};